import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import VerifiedBadge from "@/components/app/VerifiedBadge";
import { UserPlus, Users } from "lucide-react";
import { toast } from "sonner";

export default function Network() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [discover, setDiscover] = useState([]);

  const load = async () => {
    try {
      const [{ data: f1 }, { data: f2 }, { data: d }] = await Promise.all([
        api.get(`/follow/followers/${user.user_id}`),
        api.get(`/follow/following/${user.user_id}`),
        api.get("/discover/doctors", { params: user?.country ? { country: user.country } : {} }),
      ]);
      setFollowers(f1);
      setFollowing(f2);
      setDiscover(d.filter((u) => u.user_id !== user.user_id));
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    }
  };
  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [user]);

  const toggleFollow = async (uid, isFollowing) => {
    try {
      if (isFollowing) await api.delete(`/follow/${uid}`);
      else await api.post(`/follow/${uid}`);
      load();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    }
  };

  const UserCard = ({ u, isFollowing }) => (
    <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3" data-testid={`net-user-${u.user_id}`}>
      <Link to={`/u/${u.user_id}`}>
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#618DB8] to-[#2A4365] text-white flex items-center justify-center font-bold flex-shrink-0">
          {(u.name || "?").replace(/^Dr\s+/, "").charAt(0)}
        </div>
      </Link>
      <div className="flex-1 min-w-0">
        <Link to={`/u/${u.user_id}`} className="font-display font-bold text-deep hover:text-[#618DB8] flex items-center">
          {u.name} {u.blue_check && <VerifiedBadge size={14} />}
        </Link>
        <div className="text-xs text-slate-500">{u.specialty} · {u.city}</div>
      </div>
      <Button size="sm" onClick={() => toggleFollow(u.user_id, isFollowing)} className={isFollowing ? "btn-deep" : "btn-coral"} data-testid={`toggle-follow-${u.user_id}`}>
        {isFollowing ? "Abonné" : "Suivre"}
      </Button>
    </div>
  );

  const isPatient = user?.role === "patient";
  const titleLabel = isPatient ? "Mes praticiens" : "Mon réseau";
  const discoverLabel = isPatient ? "Trouver mes praticiens" : "Découvrir des confrères";
  const followingLabel = isPatient ? "Praticiens suivis" : "Abonnements";
  const followersLabel = isPatient ? "Me suivent" : "Abonnés";

  return (
    <div className="p-6 lg:p-10 max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="font-display text-3xl font-extrabold text-deep">{titleLabel}</h1>
        <p className="text-sm text-slate-500 mt-1">
          {isPatient ? "Suivez les praticiens qui vous accompagnent dans votre parcours de santé." : "Connectez-vous à votre communauté de confrères."}
        </p>
      </div>
      <Tabs defaultValue="discover">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="discover" data-testid="tab-discover"><UserPlus className="h-4 w-4 me-1.5" />{discoverLabel}</TabsTrigger>
          <TabsTrigger value="following" data-testid="tab-following">{followingLabel} ({following.length})</TabsTrigger>
          {!isPatient && <TabsTrigger value="followers" data-testid="tab-followers"><Users className="h-4 w-4 me-1.5" />{followersLabel} ({followers.length})</TabsTrigger>}
        </TabsList>
        <TabsContent value="discover" className="mt-4 space-y-3">
          {discover.length === 0 ? <p className="text-slate-500 text-sm">{isPatient ? "Aucun praticien à découvrir dans votre pays." : "Aucun confrère à découvrir."}</p> :
            discover.map((u) => <UserCard key={u.user_id} u={u} isFollowing={following.some((f) => f.user_id === u.user_id)} />)}
        </TabsContent>
        <TabsContent value="following" className="mt-4 space-y-3">
          {following.length === 0 ? <p className="text-slate-500 text-sm">{isPatient ? "Vous ne suivez aucun praticien pour le moment." : t("network.no_following")}</p> :
            following.map((u) => <UserCard key={u.user_id} u={u} isFollowing={true} />)}
        </TabsContent>
        {!isPatient && (
          <TabsContent value="followers" className="mt-4 space-y-3">
            {followers.length === 0 ? <p className="text-slate-500 text-sm">{t("network.no_followers")}</p> :
              followers.map((u) => <UserCard key={u.user_id} u={u} isFollowing={following.some((f) => f.user_id === u.user_id)} />)}
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
